import type { SidePanelView } from '@/lib/side-panel/side-panel-items';
import { reorderTabs } from '@/lib/side-panel/tab-drag';

/**
 * One open tab of the side panel: the view it shows and its own id. A view that can be open
 * more than once (a browser tab, a terminal) keys the data of each instance by this id.
 */
export type PanelTab = { id: string; view: SidePanelView };

/**
 * The side panel's tabs in strip order, the one shown, and the ids in the order they were last
 * shown (most recent first), so that showing a view again returns to its last tab.
 */
export type PanelTabs = {
  tabs: readonly PanelTab[];
  activeId: string | null;
  recent: readonly string[];
};

export const NO_PANEL_TABS: PanelTabs = { tabs: [], activeId: null, recent: [] };

function newTabId(): string {
  return crypto.randomUUID();
}

export function activePanelTab(state: PanelTabs): PanelTab | null {
  return state.tabs.find((tab) => tab.id === state.activeId) ?? null;
}

/** The view the side panel shows, `null` when no tab is open. */
export function activePanelView(state: PanelTabs): SidePanelView | null {
  return activePanelTab(state)?.view ?? null;
}

export function panelTabsOf(state: PanelTabs, view: SidePanelView): PanelTab[] {
  return state.tabs.filter((tab) => tab.view === view);
}

/** The open tab of `view` shown last, if any. */
export function lastPanelTabOf(state: PanelTabs, view: SidePanelView): PanelTab | null {
  const tabs = panelTabsOf(state, view);
  for (const id of state.recent) {
    const tab = tabs.find((candidate) => candidate.id === id);
    if (tab !== undefined) return tab;
  }
  return tabs[0] ?? null;
}

export function activatePanelTab(state: PanelTabs, id: string): PanelTabs {
  if (state.activeId === id || !state.tabs.some((tab) => tab.id === id)) return state;
  return { ...state, activeId: id, recent: [id, ...state.recent.filter((other) => other !== id)] };
}

/** Opens `tab` at the end of the strip unless it is open already, and shows it. */
export function openPanelTab(state: PanelTabs, tab: PanelTab): PanelTabs {
  const opened = state.tabs.some((candidate) => candidate.id === tab.id)
    ? state
    : { ...state, tabs: [...state.tabs, tab] };
  return activatePanelTab(opened, tab.id);
}

/** Shows `view`: its last shown tab when one is open, a new tab otherwise. */
export function showPanelView(
  state: PanelTabs,
  view: SidePanelView,
  createId: () => string = newTabId,
): PanelTabs {
  if (activePanelView(state) === view) return state;
  const existing = lastPanelTabOf(state, view);
  return existing === null
    ? openPanelTab(state, { id: createId(), view })
    : activatePanelTab(state, existing.id);
}

/** Keeps the tabs `keep` accepts; a removed active tab gives way to its neighbour in the strip. */
export function retainPanelTabs(state: PanelTabs, keep: (tab: PanelTab) => boolean): PanelTabs {
  const tabs = state.tabs.filter(keep);
  if (tabs.length === state.tabs.length) return state;
  const recent = state.recent.filter((id) => tabs.some((tab) => tab.id === id));
  if (tabs.some((tab) => tab.id === state.activeId)) return { ...state, tabs, recent };
  const activeIndex = state.tabs.findIndex((tab) => tab.id === state.activeId);
  const neighbour =
    activeIndex < 0
      ? undefined
      : (state.tabs.slice(activeIndex + 1).find(keep) ??
        state.tabs.slice(0, activeIndex).reverse().find(keep));
  const activeId = neighbour?.id ?? null;
  return {
    tabs,
    activeId,
    recent: activeId === null ? recent : [activeId, ...recent.filter((id) => id !== activeId)],
  };
}

export function closePanelTab(state: PanelTabs, id: string): PanelTabs {
  return retainPanelTabs(state, (tab) => tab.id !== id);
}

/** Moves the dragged tab next to `targetId`, on the side it came from. */
export function movePanelTab(state: PanelTabs, draggedId: string, targetId: string): PanelTabs {
  if (draggedId === targetId) return state;
  const order = reorderTabs(
    state.tabs.map((tab) => tab.id),
    draggedId,
    targetId,
  );
  return {
    ...state,
    tabs: order.flatMap((id) => state.tabs.filter((tab) => tab.id === id)),
  };
}

/**
 * Where a surface placing its own tabs shows (the browser's pages, the terminals): hidden unless
 * one of its tabs shows, else the whole panel, laid over the split's cells and letting the
 * pointer through to the cells it leaves bare. Of a view whose tabs two surfaces show (the
 * terminals: the shells, the agent's commands), `owns` picks this surface's tabs, so that each
 * shows only while one of its own tabs does and never stacks under the other.
 */
export function surfaceSlotClass({
  shownTabs,
  view,
  split,
  owns = () => true,
}: {
  shownTabs: readonly PanelTab[];
  view: SidePanelView | null | undefined;
  split: boolean;
  owns?: (tabId: string) => boolean;
}): string {
  if (view === null || view === undefined) return 'hidden';
  if (!shownTabs.some((tab) => tab.view === view && owns(tab.id))) return 'hidden';
  return split ? 'pointer-events-none absolute inset-0 z-10' : 'h-full';
}
